/**
 * Pure cache update utilities for the planning projects cache.
 *
 * No React, no hooks — plain data transforms on PlanningProjectsResponse.
 * Used by mutation hooks (useCreateInterval, useUpdateInterval, useDeleteInterval)
 * to apply optimistic updates before the server responds.
 */
import { Interval, Project, ProjectClosedDay, ProjectMilestone, Task } from '@/types/types';

import { PlanningInterval, PlanningProject, PlanningResource, PlanningTask } from './types';

export type PlanningCache = PlanningProject[];

// ─────────────────────────────────────────────────────────────────────────────
// Reorder task categories
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Moves a task category to a new index within a project's task_categories list.
 * All sibling positions are rewritten to reflect the new order (0-indexed).
 * If the project or category is not found, returns cache unchanged.
 */
export const applyTaskCategoryReorder = (
  cache: PlanningCache,
  projectId: number | string,
  taskCategoryId: number | string,
  newIndex: number
): PlanningCache =>
  cache.map((project) => {
    if (project.id !== projectId) return project;

    const currentIndex = project.task_categories.findIndex((c) => c.id === taskCategoryId);
    if (currentIndex === -1) return project;

    const categories = [...project.task_categories];
    const [moved] = categories.splice(currentIndex, 1);
    categories.splice(newIndex, 0, moved);

    return {
      ...project,
      task_categories: categories.map((c, i) => ({ ...c, position: i })),
    };
  });

/**
 * Moves a fag to a new index within a project's trades list.
 * All sibling positions are rewritten to reflect the new order (0-indexed).
 * If the project or fag is not found, returns cache unchanged.
 */
export const applyTradeReorder = (
  cache: PlanningCache,
  projectId: number | string,
  tradeId: number | string,
  newIndex: number
): PlanningCache =>
  cache.map((project) => {
    if (project.id !== projectId || !project.trades) return project;

    const currentIndex = project.trades.findIndex((t) => t.id === tradeId);
    if (currentIndex === -1) return project;

    const trades = [...project.trades];
    const [moved] = trades.splice(currentIndex, 1);
    trades.splice(newIndex, 0, moved);

    return { ...project, trades: trades.map((t, i) => ({ ...t, position: i })) };
  });

/**
 * Moves a location to a new index within a project's locations list.
 * All sibling positions are rewritten to reflect the new order (0-indexed).
 * If the project or location is not found, returns cache unchanged.
 */
export const applyLocationReorder = (
  cache: PlanningCache,
  projectId: number | string,
  locationId: number | string,
  newIndex: number
): PlanningCache =>
  cache.map((project) => {
    if (project.id !== projectId) return project;

    const currentIndex = project.locations.findIndex((l) => l.id === locationId);
    if (currentIndex === -1) return project;

    const locations = [...project.locations];
    const [moved] = locations.splice(currentIndex, 1);
    locations.splice(newIndex, 0, moved);

    return {
      ...project,
      locations: locations.map((l, i) => ({ ...l, position: i })),
    };
  });

// ─────────────────────────────────────────────────────────────────────────────
// Reorder tasks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Moves a task to a new index within its project's task list.
 * All sibling positions are updated to reflect the new order (0-indexed).
 * If the task is not found, returns cache unchanged.
 */
export const applyTaskReorder = (
  cache: PlanningCache,
  taskId: number | string,
  newIndex: number
): PlanningCache =>
  cache.map((project) => {
    const currentIndex = project.tasks.findIndex((t) => t.id === taskId);
    if (currentIndex === -1) return project;

    const tasks = [...project.tasks];
    const [task] = tasks.splice(currentIndex, 1);
    tasks.splice(newIndex, 0, task);

    return {
      ...project,
      tasks: tasks.map((t, i) => ({ ...t, position: i })),
    };
  });

// ─────────────────────────────────────────────────────────────────────────────
// Create
// ─────────────────────────────────────────────────────────────────────────────

export interface OptimisticCreateVariables {
  /** The resource this interval belongs to (matched by id) */
  resourceId: string | number;
  /** The project this interval belongs to (matched by id) */
  projectId: string | number;
  interval: Omit<PlanningInterval, 'id'>;
}

/**
 * Inserts a synthetic interval into the matching resource.
 * If the resource or project is not found, returns cache unchanged.
 */
export const applyIntervalCreate = (
  cache: PlanningCache,
  variables: OptimisticCreateVariables,
  tempId: string
): PlanningCache =>
  cache.map((project) => {
    if (project.id !== variables.projectId) return project;
    return {
      ...project,
      resources: project.resources.map((resource) => {
        if (resource.id !== variables.resourceId) return resource;
        return {
          ...resource,
          intervals: [...resource.intervals, { ...variables.interval, id: tempId }],
        };
      }),
    };
  });

/**
 * Inserts or replaces an interval with a real (server) ID, matched by id
 * across all resources in the given project. Used for socket-delivered
 * interval creates: if the id is already in cache (our own write, already
 * applied by the mutation hook) this is a no-op replace; if it's not (another
 * viewer created it) it's inserted into the named resource.
 * No-op if the project or resource isn't found.
 */
export const applyIntervalUpsert = (
  cache: PlanningCache,
  projectId: PlanningProject['id'],
  resourceId: PlanningResource['id'],
  interval: PlanningInterval
): PlanningCache =>
  cache.map((project) => {
    if (project.id !== projectId) return project;

    let found = false;
    const resources = project.resources.map((resource) => {
      const index = resource.intervals.findIndex((i) => i.id === interval.id);
      if (index === -1) return resource;
      found = true;
      const intervals = resource.intervals.slice();
      intervals[index] = interval;
      return { ...resource, intervals };
    });

    if (found) return { ...project, resources };

    return {
      ...project,
      resources: resources.map((resource) =>
        resource.id === resourceId
          ? { ...resource, intervals: [...resource.intervals, interval] }
          : resource
      ),
    };
  });

// ─────────────────────────────────────────────────────────────────────────────
// Update
// ─────────────────────────────────────────────────────────────────────────────

export type IntervalPatch = Partial<Omit<PlanningInterval, 'id'>>;

/**
 * Finds an interval by ID across all projects/resources and merges the patch.
 * If the interval is not found, returns cache unchanged.
 */
export const applyIntervalUpdate = (
  cache: PlanningCache,
  intervalId: string | number,
  patch: IntervalPatch
): PlanningCache =>
  cache.map((project) => {
    let changed = false;
    const resources = project.resources.map((resource) => {
      const index = resource.intervals.findIndex((interval) => interval.id === intervalId);
      if (index === -1) return resource;
      changed = true;
      const intervals = resource.intervals.slice();
      intervals[index] = { ...intervals[index], ...patch };
      return { ...resource, intervals };
    });
    return changed ? { ...project, resources } : project;
  });

/**
 * Finds an interval by its (temp) ID and replaces it with server-confirmed fields,
 * including swapping the ID itself. Used to reconcile an optimistically-created
 * interval (temp ID) with the real record once the server responds.
 * If the interval is not found, returns cache unchanged.
 */
export const applyIntervalReplaceId = (
  cache: PlanningCache,
  tempId: string,
  patch: IntervalPatch & { id: PlanningInterval['id'] }
): PlanningCache =>
  cache.map((project) => {
    let changed = false;
    const resources = project.resources.map((resource) => {
      const index = resource.intervals.findIndex((interval) => interval.id === tempId);
      if (index === -1) return resource;
      changed = true;
      const intervals = resource.intervals.slice();
      intervals[index] = { ...intervals[index], ...patch };
      return { ...resource, intervals };
    });
    return changed ? { ...project, resources } : project;
  });

/**
 * Finds the task_id of an interval by scanning all projects/resources.
 * Returns null if the interval is not found or has no task_id.
 */
export const findTaskIdForInterval = (
  cache: PlanningCache,
  intervalId: string | number
): PlanningTask['id'] | null => {
  for (const project of cache) {
    for (const resource of project.resources) {
      const interval = resource.intervals.find((i) => i.id === intervalId);
      if (interval) return interval.task_id;
    }
  }
  return null;
};

/**
 * Recomputes a locked task's displayed date range as the min/max span of its
 * own intervals — mirrors the server-side derivation for date_lock_mode: "locked"
 * tasks (their bar comes from bookings, unlike "freed" tasks which use planned dates).
 * No-op for freed tasks, or if the task/its intervals aren't found.
 *
 * Stopgap for the optimistic phase only: mutation responses now return the
 * server-computed rollup + slack directly (see `taskPatchFromServerTask`) —
 * use that once the response lands. This is only for the instant-feedback
 * window before the server replies, where nothing better is available yet.
 */
export const applyLockedTaskDateRollup = (
  cache: PlanningCache,
  taskId: PlanningTask['id'] | null
): PlanningCache => {
  if (taskId == null) return cache;

  return cache.map((project) => {
    const taskIndex = project.tasks.findIndex((t) => t.id === taskId);
    if (taskIndex === -1) return project;

    const task = project.tasks[taskIndex];
    if (task.date_lock_mode !== 'locked') return project;

    const intervals = project.resources
      .flatMap((r) => r.intervals)
      .filter((i) => i.task_id === taskId);
    if (intervals.length === 0) return project;

    const start = intervals.reduce(
      (min, i) => (i.start_date < min ? i.start_date : min),
      intervals[0].start_date
    );
    const end = intervals.reduce(
      (max, i) => (i.end_date > max ? i.end_date : max),
      intervals[0].end_date
    );

    if (start === task.start_date && end === task.end_date) return project;

    const tasks = project.tasks.slice();
    tasks[taskIndex] = { ...task, start_date: start, end_date: end };
    return { ...project, tasks };
  });
};

export type TaskPatch = Partial<Omit<PlanningTask, 'id'>>;

/**
 * Finds a task by ID across all projects and merges the patch.
 * If the task is not found, returns cache unchanged.
 */
export const applyTaskUpdate = (
  cache: PlanningCache,
  taskId: string | number,
  patch: TaskPatch
): PlanningCache =>
  cache.map((project) => {
    const index = project.tasks.findIndex((task) => task.id === taskId);
    if (index === -1) return project;
    const tasks = project.tasks.slice();
    tasks[index] = { ...tasks[index], ...patch };
    return { ...project, tasks };
  });

/**
 * Builds a cache patch from a server-returned Task — rollup dates and
 * critical-path fields (slack_to_next_days, slack_to_finish_days, is_critical)
 * included, since write endpoints now return the server-computed values
 * directly instead of leaving them stale until the next full fetch.
 */
type GroupingKey = 'project_milestone_id' | 'task_category_id' | 'location_id' | 'trade_id';

// Task ids are typed string | number, PlanningTask ids number; the API returns numbers.
const groupingPatch = (task: Task): Pick<TaskPatch, GroupingKey> => {
  const keys: GroupingKey[] = [
    'project_milestone_id',
    'task_category_id',
    'location_id',
    'trade_id',
  ];
  return Object.fromEntries(
    keys.filter((key) => task[key] !== undefined).map((key) => [key, task[key]])
  ) as Pick<TaskPatch, GroupingKey>;
};

export const taskPatchFromServerTask = (task: Task): TaskPatch => ({
  date_lock_mode: task.date_lock_mode,
  planned_start_date: task.planned_start_date,
  planned_duration_days: task.planned_duration_days,
  ...(task.start_date != null ? { start_date: task.start_date } : {}),
  ...(task.end_date != null ? { end_date: task.end_date } : {}),
  slack_to_next_days: task.slack_to_next_days,
  slack_to_finish_days: task.slack_to_finish_days,
  is_critical: task.is_critical ?? false,
  completion_percentage: task.completion_percentage,
  ...groupingPatch(task),
});

export interface CriticalPathEntry {
  slack_to_next_days: number | null;
  slack_to_finish_days: number | null;
  is_critical: boolean | null;
}

/**
 * Merges a server-returned critical-path map (task_id -> slack/critical fields)
 * over every task it covers, across all projects. This map is whole-project —
 * slack is graph-wide, so a task's own dates not changing doesn't mean its
 * slack didn't (e.g. a predecessor's slack shifts when a successor moves,
 * even though the predecessor is never in that write's cascaded_tasks list).
 * JSON object keys are always strings, so task IDs are compared as strings.
 */
export const applyCriticalPathUpdate = (
  cache: PlanningCache,
  criticalPath: Record<string, CriticalPathEntry> | undefined
): PlanningCache => {
  if (!criticalPath) return cache;

  return cache.map((project) => {
    let changed = false;
    const tasks = project.tasks.map((task) => {
      const entry = criticalPath[String(task.id)];
      if (!entry) return task;
      changed = true;
      return {
        ...task,
        slack_to_next_days: entry.slack_to_next_days,
        slack_to_finish_days: entry.slack_to_finish_days,
        is_critical: entry.is_critical ?? false,
      };
    });
    return changed ? { ...project, tasks } : project;
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Delete
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Removes an interval by ID from whichever resource holds it.
 * If the interval is not found, returns cache unchanged.
 */
export const applyIntervalDelete = (
  cache: PlanningCache,
  intervalId: string | number
): PlanningCache =>
  cache.map((project) => ({
    ...project,
    resources: project.resources.map((resource) => ({
      ...resource,
      intervals: resource.intervals.filter((interval) => interval.id !== intervalId),
    })),
  }));

// ─────────────────────────────────────────────────────────────────────────────
// Resources (assignment changes — who's on a task, not booking dates)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Inserts or replaces a resource by ID within the given project.
 * If the project is not found, returns cache unchanged.
 */
export const applyResourceUpsert = (
  cache: PlanningCache,
  projectId: PlanningProject['id'],
  resource: PlanningResource
): PlanningCache =>
  cache.map((project) => {
    if (project.id !== projectId) return project;

    const index = project.resources.findIndex((r) => r.id === resource.id);
    const resources = project.resources.slice();
    if (index === -1) {
      resources.push(resource);
    } else {
      resources[index] = resource;
    }
    return { ...project, resources };
  });

/**
 * Removes a resource by ID from the given project.
 * If the project is not found, returns cache unchanged.
 */
export const applyResourceDelete = (
  cache: PlanningCache,
  projectId: PlanningProject['id'],
  resourceId: PlanningResource['id']
): PlanningCache =>
  cache.map((project) => {
    if (project.id !== projectId) return project;
    return { ...project, resources: project.resources.filter((r) => r.id !== resourceId) };
  });

// ─────────────────────────────────────────────────────────────────────────────
// PlanningChannel broadcast
// ─────────────────────────────────────────────────────────────────────────────

export interface PlanningPresencePerson {
  id: number;
  name: string;
  photo_url: string;
}

export interface PlanningBroadcastPayload {
  type:
    | 'task_updated'
    | 'interval_created'
    | 'interval_updated'
    | 'interval_deleted'
    | 'resource_created'
    | 'resource_updated'
    | 'resource_deleted'
    | 'presence_updated'
    | 'project_closed_days_updated'
    | 'task_created'
    | 'task_deleted'
    | 'project_milestones_updated'
    | 'task_dependencies_updated'
    | 'project_updated'
    | 'task_categories_reordered'
    | 'trades_reordered'
    | 'locations_reordered';
  task?: Task | null;
  tasks?: Task[];
  task_id?: Task['id'];
  change?: 'created' | 'updated' | 'destroyed';
  project?: Pick<Project, 'id' | 'name' | 'status' | 'start_date' | 'end_date'>;
  project_milestones?: ProjectMilestone[];
  task_categories?: GroupItem[];
  trades?: GroupItem[];
  locations?: GroupItem[];
  interval?: Interval | null;
  resource?: PlanningResource | null;
  presence?: PlanningPresencePerson[];
  cascaded_tasks?: Task[];
  cascaded_intervals?: Interval[];
  critical_path?: Record<string, CriticalPathEntry>;
  project_id?: number | string;
  project_closed_days?: ProjectClosedDay[];
}

type GroupItem = { id: number | string } & Record<string, unknown>;

/**
 * Applies a reordered group list from the broadcast: the server's order and
 * fields win, but a cached item keeps any field the payload doesn't carry.
 */
const applyGroupOrder = <T extends { id: number | string }>(
  cached: T[] | undefined,
  items: GroupItem[]
): T[] => {
  const byId = new Map((cached ?? []).map((item) => [item.id, item]));
  return items.map((item) => ({ ...byId.get(item.id), ...item }) as unknown as T);
};

const mapProject = (
  cache: PlanningCache,
  projectId: number | string,
  update: (project: PlanningProject) => PlanningProject
): PlanningCache => cache.map((project) => (project.id === projectId ? update(project) : project));

// The wire Task is the same TaskSerializer shape the planning index uses (position
// included, start/end null until it has bookings), so it maps over directly.
const planningTaskFromWireTask = (task: Task): PlanningTask =>
  ({
    id: task.id,
    name: task.name,
    project_id: task.project_id,
    description: task.description,
    status: task.status,
    position: (task as Task & { position?: number }).position ?? 0,
    start_date: task.start_date,
    end_date: task.end_date,
    date_lock_mode: task.date_lock_mode,
    planned_start_date: task.planned_start_date,
    planned_duration_days: task.planned_duration_days,
    project_milestone_id: task.project_milestone_id,
    task_category_id: task.task_category_id,
    location_id: task.location_id,
    trade_id: task.trade_id,
    completion_percentage: task.completion_percentage,
    slack_to_next_days: task.slack_to_next_days,
    slack_to_finish_days: task.slack_to_finish_days,
    is_critical: task.is_critical ?? false,
  }) as PlanningTask;

const applyTaskCreated = (cache: PlanningCache, task: Task): PlanningCache => {
  if (task.project_id == null) return cache;
  const created = planningTaskFromWireTask(task);
  return mapProject(cache, task.project_id, (project) => {
    if (project.tasks.some((t) => t.id === task.id)) return project;
    const index = project.tasks.findIndex((t) => t.position >= created.position);
    const tasks = project.tasks.slice();
    tasks.splice(index === -1 ? tasks.length : index, 0, created);
    return { ...project, tasks };
  });
};

const intervalPatchFromWireInterval = (interval: Interval): IntervalPatch => ({
  comment: interval.comment,
  hours_each_day: interval.hours_each_day,
  start_date: interval.start_date,
  end_date: interval.end_date,
  task_id: interval.task_id,
  completed: interval.completed,
  exclude_non_working_days: interval.exclude_non_working_days,
});

const planningIntervalFromWireInterval = (interval: Interval): PlanningInterval => ({
  id: interval.id,
  start_date: interval.start_date,
  end_date: interval.end_date,
  hours_each_day: interval.hours_each_day,
  comment: interval.comment,
  completed: interval.completed,
  exclude_non_working_days: interval.exclude_non_working_days,
  completion_percentage: 0,
  task_id: interval.task_id,
  project_id: interval.project_id,
  comments_count: 0,
});

/**
 * Replaces a project's cached closed-days list wholesale (the broadcast
 * always carries the full current list, so this is a set, not a merge).
 * No-op if the project isn't found.
 */
export const applyProjectClosedDaysUpdate = (
  cache: PlanningCache,
  projectId: number | string,
  projectClosedDays: ProjectClosedDay[]
): PlanningCache =>
  cache.map((project) =>
    project.id === projectId ? { ...project, project_closed_days: projectClosedDays } : project
  );

/**
 * Applies one PlanningChannel broadcast payload to cache — the single code
 * path for every write kind, whether it's our own echoed write or another
 * viewer's. Mirrors the merge each mutation hook already does in onSuccess;
 * this is the version driven by the socket instead of the HTTP response.
 */
export const applyPlanningBroadcast = (
  cache: PlanningCache,
  payload: PlanningBroadcastPayload
): PlanningCache => {
  let next = cache;

  switch (payload.type) {
    case 'interval_created':
      if (payload.interval) {
        next = applyIntervalUpsert(
          next,
          payload.interval.project_id,
          payload.interval.intervalable_id,
          planningIntervalFromWireInterval(payload.interval)
        );
      }
      break;
    case 'interval_updated':
      if (payload.interval) {
        next = applyIntervalUpdate(
          next,
          payload.interval.id,
          intervalPatchFromWireInterval(payload.interval)
        );
      }
      break;
    case 'interval_deleted':
      if (payload.interval) {
        next = applyIntervalDelete(next, payload.interval.id);
      }
      break;
    case 'resource_created':
    case 'resource_updated':
      if (payload.resource) {
        next = applyResourceUpsert(next, payload.resource.project_id, payload.resource);
      }
      break;
    case 'resource_deleted':
      if (payload.resource) {
        next = applyResourceDelete(next, payload.resource.project_id, payload.resource.id);
      }
      break;
    case 'project_closed_days_updated':
      if (payload.project_id != null && payload.project_closed_days) {
        next = applyProjectClosedDaysUpdate(next, payload.project_id, payload.project_closed_days);
      }
      break;
    case 'task_deleted':
      if (payload.project_id != null && payload.task_id != null) {
        next = mapProject(next, payload.project_id, (project) => ({
          ...project,
          tasks: project.tasks.filter((t) => t.id !== payload.task_id),
          resources: project.resources.filter((r) => r.task_id !== payload.task_id),
        }));
      }
      break;
    case 'project_milestones_updated':
      if (payload.project_id != null && payload.project_milestones) {
        const milestones = payload.project_milestones;
        next = mapProject(next, payload.project_id, (project) => ({ ...project, milestones }));
      }
      break;
    case 'project_updated':
      if (payload.project_id != null && payload.project) {
        const { name, status, start_date, end_date } = payload.project;
        next = mapProject(next, payload.project_id, (project) => ({
          ...project,
          name,
          status,
          start_date,
          end_date,
        }));
      }
      break;
    case 'task_categories_reordered':
      if (payload.project_id != null && payload.task_categories) {
        const items = payload.task_categories;
        next = mapProject(next, payload.project_id, (project) => ({
          ...project,
          task_categories: applyGroupOrder(project.task_categories, items),
        }));
      }
      break;
    case 'trades_reordered':
      if (payload.project_id != null && payload.trades) {
        const items = payload.trades;
        next = mapProject(next, payload.project_id, (project) => ({
          ...project,
          trades: applyGroupOrder(project.trades, items),
        }));
      }
      break;
    case 'locations_reordered':
      if (payload.project_id != null && payload.locations) {
        const items = payload.locations;
        next = mapProject(next, payload.project_id, (project) => ({
          ...project,
          locations: applyGroupOrder(project.locations, items),
        }));
      }
      break;
    case 'task_created':
      if (payload.task) next = applyTaskCreated(next, payload.task);
      break;
    case 'task_dependencies_updated':
    case 'task_updated':
    case 'presence_updated':
      // presence_updated carries no planningProjects cache data — it's
      // ephemeral UI state (usePlanningPresenceStore), handled directly in
      // usePlanningChannels rather than here.
      break;
  }

  if (payload.task) {
    next = applyTaskUpdate(next, payload.task.id, taskPatchFromServerTask(payload.task));
  }
  for (const endpoint of payload.tasks ?? []) {
    next = applyTaskUpdate(next, endpoint.id, taskPatchFromServerTask(endpoint));
  }
  for (const cascaded of payload.cascaded_tasks ?? []) {
    next = applyTaskUpdate(next, cascaded.id, taskPatchFromServerTask(cascaded));
  }
  // A cascaded task's own rolled-up dates aren't enough — its actual booking
  // bars (what a resource row renders) live on its intervals, which shifted
  // too (TaskCascadeRescheduleService moves interval rows directly).
  for (const cascadedInterval of payload.cascaded_intervals ?? []) {
    next = applyIntervalUpdate(
      next,
      cascadedInterval.id,
      intervalPatchFromWireInterval(cascadedInterval)
    );
  }

  return applyCriticalPathUpdate(next, payload.critical_path);
};

// ─────────────────────────────────────────────────────────────────────────────
// Split (delete original + insert N new segments)
// ─────────────────────────────────────────────────────────────────────────────

export interface SplitSegment {
  start_date: string;
  end_date: string;
}

/**
 * Replaces a single interval with N weekday-only segments.
 * Finds the original resource by scanning for the interval,
 * then removes it and inserts the new segments with temp IDs.
 */
export const applyIntervalSplit = (
  cache: PlanningCache,
  originalIntervalId: string | number,
  segments: SplitSegment[],
  tempIds: string[]
): PlanningCache => {
  // First pass: find the original interval to inherit its properties
  let source: PlanningInterval | null = null;
  let sourceResource: PlanningResource | null = null;

  outer: for (const project of cache) {
    for (const resource of project.resources) {
      const found = resource.intervals.find((i) => i.id === originalIntervalId);
      if (found) {
        source = found;
        sourceResource = resource;
        break outer;
      }
    }
  }

  if (!source || !sourceResource) return cache;

  const newIntervals: PlanningInterval[] = segments.map((seg, idx) => ({
    id: tempIds[idx],
    start_date: seg.start_date,
    end_date: seg.end_date,
    hours_each_day: source!.hours_each_day,
    comment: source!.comment,
    completed: source!.completed,
    completion_percentage: source!.completion_percentage,
    task_id: source!.task_id,
    project_id: source!.project_id,
    comments_count: 0,
  }));

  return cache.map((project) => ({
    ...project,
    resources: project.resources.map((resource) => {
      if (resource.id !== sourceResource!.id) return resource;
      return {
        ...resource,
        intervals: [
          ...resource.intervals.filter((i) => i.id !== originalIntervalId),
          ...newIntervals,
        ],
      };
    }),
  }));
};
